import Currencies from 'currency-codes';

import { NO_DATA_DISPLAY } from '@utils/data';

import { getLocaleFromCurrency } from './locale';

export const EMPTY_VALUE = '-';
export const DEFAULT_CURRENCY = 'USD';
export const DEFAULT_CURRENCY_SYMBOL = '$';
export const DEFAULT_CURRENCY_DECIMAL_SEPARATOR = '.';
export const DEFAULT_CURRENCY_THOUSAND_SEPARATOR = ',';
export const DEFAULT_PRECISION = 4;

const fixWhitespace = (str: string): string => str.replace(/\s/g, ' ');

export const validateCurrency = (currency = DEFAULT_CURRENCY) => {
  let currencyObj;

  if (currency) {
    currencyObj = Currencies.code(currency);
  }

  return currencyObj ? currencyObj.code : DEFAULT_CURRENCY;
};

export const getCurrencyFormatter = (
  currency: string,
  fractionDigits: number,
  options: Intl.NumberFormatOptions = {},
) => (
  new Intl.NumberFormat(getLocaleFromCurrency(currency), {
    currency,
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
    style: 'currency',
    ...options,
  })
);

// Should review currencies/formats other than USD. this will try to plug in the other currency code
// and it seems to work fine for EUR but there might be problems with other currencies.
// If you want anything to be rounded, put in the number to round by as roundOver
export const displayCurrency = (
  amount: number,
  currency = DEFAULT_CURRENCY,
  minimumFractionDigits = 0,
  maximumFractionDigits = DEFAULT_PRECISION,
  roundOver = Number.MAX_VALUE,
  options: Intl.NumberFormatOptions = {}
) => {
  if (Number.isNaN(amount)) {
    return EMPTY_VALUE;
  }

  const cleanCurrency = validateCurrency(currency);  // handle true null currency state more gracefully
  const shouldRound = amount > roundOver;
  const formatter = Intl.NumberFormat(
    getLocaleFromCurrency(cleanCurrency),
    {
      currency: cleanCurrency,
      maximumFractionDigits: shouldRound ? 0 : maximumFractionDigits,
      minimumFractionDigits: shouldRound ? 0 : minimumFractionDigits,
      style: 'currency',
      ...options
    }
  );

  return fixWhitespace(formatter.format(amount));
};

export const displayCurrencySymbol = (
  currency = DEFAULT_CURRENCY,
) => {

  const cleanCurrency = validateCurrency(currency);  // handle true null currency state more gracefully
  const formatter = getCurrencyFormatter(cleanCurrency, 0);

  // We pass in `0` to get the currency symbol
  const currencySymbol = formatter.formatToParts(0).find(part => part.type === 'currency')?.value;
  return currencySymbol || cleanCurrency;
};

/**
 * Compact currency for narrow columns and stat tiles — renders as "$120K",
 * "$1.2M", "$980". Sub-thousand values get zero fractional digits; $1K and
 * above get one. Uses `en-US` as the locale regardless of the passed currency
 * so the compact abbreviation ("K", "M", "B") is stable in table cells across
 * tenants — locale-driven fallbacks (e.g. de-DE + EUR) drop out of compact
 * form entirely on some ICU builds and render "120.000 €" instead, which
 * would be wider than the currency column can hold. `validateCurrency` still
 * runs so the symbol swap for the passed currency is correct.
 */
export const formatCurrencyCompact = (
  amount: number | null | undefined,
  currency: string = DEFAULT_CURRENCY,
): string => {
  if (amount == null || !Number.isFinite(amount)) return NO_DATA_DISPLAY;
  const cleanCurrency = validateCurrency(currency);
  const maximumFractionDigits = Math.abs(amount) >= 1000 ? 1 : 0;
  return fixWhitespace(new Intl.NumberFormat('en-US', {
    compactDisplay: 'short',
    currency: cleanCurrency,
    maximumFractionDigits,
    minimumFractionDigits: 0,
    notation: 'compact',
    style: 'currency',
  }).format(amount));
};

export const displayCurrencyWithPrecision = (
  amount: number,
  currency = DEFAULT_CURRENCY,
  precision = DEFAULT_PRECISION,
) => {
  if (Number.isNaN(amount)) {
    return EMPTY_VALUE;
  }

  const cleanCurrency = validateCurrency(currency);  // handle true null currency state more gracefully
  const formatter = getCurrencyFormatter(cleanCurrency, precision);

  return fixWhitespace(formatter.format(amount));
};
